/* Email → Log: every message queued or sent, with status filters, details (body while Log-only is active) and resend. */
import { useState } from "react";
import { useEmailLog, useEmailLogs, useEmailTemplates, useResendEmail } from "../../../data/hooks";
import type { EmailLog, EmailStatus } from "../../../data/types";
import { useI18n } from "../../../i18n";
import { Alert, Button, Card, Chip, EmptyState, Kv, Loaded, Modal, SearchBox, SelectField, errMsg, useToast } from "../../../ui";
import { Pager } from "../../time/DeviceDetail";

export const STATUS_L: Record<EmailStatus, [string, string, string]> = {
  queued: ["Queued", "في الانتظار", "warn"], sending: ["Sending", "قيد الإرسال", "info"], sent: ["Sent", "أُرسلت", "ok"], failed: ["Failed", "فشلت", "bad"], suppressed: ["Suppressed", "محظورة", ""],
};
const when = (s: string | null) => (s ? s.slice(0, 16).replace("T", " ") : "—");

export function EmailStatusChip({ s }: { s: EmailStatus }) {
  const { L } = useI18n();
  const [en, ar, tone] = STATUS_L[s] ?? [s, s, ""];
  return <Chip tone={tone}>{L(en, ar)}</Chip>;
}

function LogDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { L } = useI18n();
  const toast = useToast();
  const q = useEmailLog(id);
  const resend = useResendEmail();
  const [view, setView] = useState<"html" | "text">("html");
  return (
    <Modal title={L("Email details", "تفاصيل الرسالة")} onClose={onClose} className="wide" testId="email-detail"
      foot={<>
        <Button onClick={onClose}>{L("Close", "إغلاق")}</Button>
        {q.data && q.data.status !== "sending" && <Button variant="pri" icon="refresh" loading={resend.isPending} data-testid="detail-resend"
          onClick={async () => { try { await resend.mutateAsync(id); toast(L("Queued for sending again", "أُعيدت إلى قائمة الإرسال")); onClose(); } catch (e) { toast(errMsg(e), "bad"); } }}>{L("Resend", "إعادة الإرسال")}</Button>}
      </>}>
      <Loaded q={q}>{(m) => (
        <div className="stack">
          <Kv items={[
            [L("To", "إلى"), <span key="to" dir="ltr">{m.toName ? m.toName + " <" + m.to + ">" : m.to}</span>],
            [L("Subject", "الموضوع"), <span key="s" dir="auto" data-testid="detail-subject">{m.subject}</span>],
            [L("Template", "القالب"), <code key="t">{m.template}</code>],
            [L("Company", "المنشأة"), m.tenant],
            [L("Status", "الحالة"), <EmailStatusChip key="st" s={m.status} />],
            [L("Attempts", "المحاولات"), <span key="a" className="num">{m.attempts}</span>],
            [L("Provider", "المزوّد"), <span key="p" dir="ltr">{m.provider || "—"}{m.providerId ? " · " + m.providerId : ""}</span>],
            [L("Created", "أُنشئت"), when(m.createdAt)],
            [L("Sent", "أُرسلت"), when(m.sentAt)],
            ...(m.redirectedFrom ? [[L("Sandbox — originally to", "وضع التجربة — المستلم الأصلي"), <span key="r" dir="ltr">{m.redirectedFrom}</span>]] as [string, JSX.Element][] : []),
          ]} />
          {m.error && <Alert tone="bad" title={L("Last error", "آخر خطأ")} desc={<code dir="ltr" style={{ wordBreak: "break-all" }}>{m.error}</code>} testId="detail-error" />}
          {m.bodyHidden ? (
            <Alert tone="info" tint title={L("Message body not stored", "نص الرسالة غير محفوظ")} desc={L("For privacy, bodies are kept only while the Log only provider is active.", "للخصوصية تُحفظ نصوص الرسائل فقط أثناء استخدام وضع السجل فقط.")} testId="detail-body-hidden" />
          ) : (
            <>
              <div className="row" style={{ gap: 6 }}>
                <Button variant={"sm " + (view === "html" ? "pri" : "ghost")} onClick={() => setView("html")} data-testid="detail-view-html">HTML</Button>
                <Button variant={"sm " + (view === "text" ? "pri" : "ghost")} onClick={() => setView("text")} data-testid="detail-view-text">{L("Plain text", "نص عادي")}</Button>
              </div>
              {view === "html"
                ? <iframe className="email-frame" title={m.subject} sandbox="" srcDoc={m.html ?? ""} data-testid="detail-html" />
                : <pre className="code-block" style={{ whiteSpace: "pre-wrap" }} dir="auto" data-testid="detail-text">{m.text}</pre>}
            </>
          )}
        </div>
      )}</Loaded>
    </Modal>
  );
}

export default function LogTab() {
  const { L } = useI18n();
  const toast = useToast();
  const [status, setStatus] = useState<EmailStatus | "">("");
  const [template, setTemplate] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string | null>(null);
  const tq = useEmailTemplates();
  const q = useEmailLogs({ status, template, to: to.trim(), page, pageSize: 25 });
  const resend = useResendEmail();
  const [busy, setBusy] = useState<string | null>(null);
  const doResend = async (m: EmailLog) => {
    setBusy(m.id);
    try { await resend.mutateAsync(m.id); toast(L("Queued for sending again", "أُعيدت إلى قائمة الإرسال")); } catch (e) { toast(errMsg(e), "bad"); } finally { setBusy(null); }
  };
  return (
    <Card flush testId="email-log">
      <div className="toolbar" style={{ padding: 12, gap: 8, flexWrap: "wrap" }}>
        <SearchBox value={to} onChange={(v) => { setTo(v); setPage(1); }} placeholder={L("Search recipient", "ابحث بالمستلم")} testId="log-to" />
        <SelectField label={L("Status", "الحالة")} value={status} onChange={(v) => { setStatus(v as EmailStatus | ""); setPage(1); }} testId="log-status"
          options={[["", L("All statuses", "كل الحالات")], ...(Object.keys(STATUS_L) as EmailStatus[]).map((k) => [k, L(STATUS_L[k][0], STATUS_L[k][1])] as [string, string])]} />
        <SelectField label={L("Template", "القالب")} value={template} onChange={(v) => { setTemplate(v); setPage(1); }} testId="log-template"
          options={[["", L("All templates", "كل القوالب")], ...(tq.data ?? []).map((t) => [t.key, L(t.subject.en, t.subject.ar).replace(/\{\{[^}]*\}\}/g, "…") + " (" + t.key + ")"] as [string, string])]} />
      </div>
      <Loaded q={q}>{(pg) => pg.items.length === 0
        ? <EmptyState title={L("No emails yet", "لا توجد رسائل بعد")} desc={status || template || to ? L("Nothing matches these filters.", "لا شيء يطابق هذه المرشحات.") : L("Emails appear here as soon as they are queued.", "تظهر الرسائل هنا فور إضافتها لقائمة الإرسال.")} />
        : (
          <>
            <div className="tw">
              <table className="tbl cards" data-testid="email-log-table">
                <thead><tr><th>{L("Recipient", "المستلم")}</th><th>{L("Subject", "الموضوع")}</th><th className="hide-md">{L("Company", "المنشأة")}</th><th>{L("Status", "الحالة")}</th><th className="hide-md">{L("Time", "الوقت")}</th><th className="end" aria-label={L("Actions", "الإجراءات")} /></tr></thead>
                <tbody>
                  {pg.items.map((m) => (
                    <tr key={m.id} data-testid={"log-" + m.id}>
                      <td className="k1"><b dir="ltr">{m.to}</b><div className="xs muted"><code>{m.template}</code>{m.redirectedFrom ? " · ↪ " + m.redirectedFrom : ""}</div></td>
                      <td className="x" data-l={L("Subject", "الموضوع")} dir="auto">{m.subject}{m.error && <div className="xs" style={{ color: "var(--danger)", wordBreak: "break-all" }} dir="ltr">{m.error}</div>}</td>
                      <td className="hide-md">{m.tenant}</td>
                      <td className="k2"><EmailStatusChip s={m.status} />{m.attempts > 1 && <span className="xs muted"> ×{m.attempts}</span>}</td>
                      <td className="hide-md num xs">{when(m.sentAt ?? m.createdAt)}</td>
                      <td className="end k3" data-l="">
                        <div className="row" style={{ gap: 4, justifyContent: "flex-end" }}>
                          <Button variant="ghost sm" icon="eye" onClick={() => setOpen(m.id)} data-testid={"log-view-" + m.id}>{L("View", "عرض")}</Button>
                          {(m.status === "failed" || m.status === "sent" || m.status === "suppressed") && <Button variant="ghost sm" icon="refresh" loading={busy === m.id} onClick={() => doResend(m)} data-testid={"log-resend-" + m.id}>{L("Resend", "إعادة")}</Button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager page={page} size={25} total={pg.total} onPage={setPage} />
          </>
        )}</Loaded>
      {open && <LogDetail id={open} onClose={() => setOpen(null)} />}
    </Card>
  );
}
